import { getGroup, getGroupInvitees, getGroupMembers, getGroups } from './http/access-ws/groups';
import type { QueryClient } from '@tanstack/query-core';
import type { DefaultError, UseQueryOptions } from '@tanstack/react-query';
import type { Group } from '@thinkalpha/ui-types/types/admin/groups';

const GROUPS = 'groups';
const BATCH = 'batch';
// Legacy keys shared with the React group cells' invalidations.
const GROUP_MEMBERSHIPS = 'groupMemberships';
const GROUP_INVITEES = 'groupInvitees';

export const getGroupQuery = (groupId: string | undefined) => {
    return {
        queryKey: [GROUPS, groupId],
        queryFn: () => getGroup(groupId!),
        enabled: Boolean(groupId),
    };
};

export const getGroupsQuery = (): UseQueryOptions<Group[], DefaultError, Group[]> => {
    return {
        queryKey: [GROUPS],
        queryFn: () => getGroups(),
    };
};

export const getGroupsByIdQuery = (
    groupIds: string[] | null,
    queryClient: QueryClient,
): UseQueryOptions<Group[], Error, Group[]> => {
    const sorted = groupIds ? [...groupIds].sort() : [];
    return {
        queryKey: [GROUPS, BATCH, ...sorted],
        queryFn: async () => {
            if (sorted.length === 0) return [];
            return Promise.all(sorted.map((id) => queryClient.fetchQuery(getGroupQuery(id))));
        },
        enabled: Boolean(groupIds) && Array.isArray(groupIds),
    };
};

export const getGroupMembersQuery = (groupId: string | undefined) => ({
    queryKey: [GROUP_MEMBERSHIPS, groupId],
    queryFn: () => getGroupMembers(groupId!),
    enabled: Boolean(groupId),
});

export const getGroupInviteesQuery = (groupId: string | undefined) => ({
    queryKey: [GROUP_INVITEES, groupId],
    queryFn: () => getGroupInvitees(groupId!),
    enabled: Boolean(groupId),
});

export const invalidateGroupsQuery = (queryClient: QueryClient, groupId?: string) => {
    return queryClient.invalidateQueries({ queryKey: groupId ? [GROUPS, groupId] : [GROUPS] });
};

export const invalidateGroupMembersQuery = (queryClient: QueryClient, groupId: string) => {
    return queryClient.invalidateQueries({ queryKey: [GROUP_MEMBERSHIPS, groupId] });
};
